import { Method } from "axios";
import { ActionKeyPair, IService } from "@/shared/hooks/useCustomQuery";
import api from "@/shared/api/api";
import { PaginatedResponse } from "@/features/profiles/services/profiles/types";

// This file now contains all the preset functionality

export interface PresetTranslation {
  languageCode: string;
  about: string;
}

export interface GalleryItem {
  id: string;
  before: string | null;
  after: string | null;
}

export interface CreatePresetPayload {
  packId: string;
  name: string;
  isFree: boolean;
  isBlackAndWhite: boolean;
  cover: string;
  lut: string;
  published: boolean;
  translations: PresetTranslation[];
}

export type UpdatePresetPayload = CreatePresetPayload;

export interface PresetResponse {
  id: string;
  packId: string;
  name: string;
  isFree: boolean;
  isBlackAndWhite: boolean;
  cover: string;
  lut: string;
  published: boolean;
  createdAt: string;
  createdById: string;
  lastUpdatedAt: string;
  lastUpdatedById: string | null;
  firebaseId: string;
  displayOrder: number | null;
  translations: PresetTranslation[];
}

export interface UploadGalleriesResponse {
  id: string;
  galleries: GalleryItem[];
}

export type PresetSortOptions =
  | "name_asc"
  | "published_asc"
  | "createdAt_asc"
  | "createdAt_desc";

export interface GetPresetsParams {
  // Filtros
  name?: string;
  creator?: string;
  published?: boolean;

  // Ordenação
  sort?: PresetSortOptions;

  // Paginação
  page?: number;
  limit?: number;
}

export interface IPreset {
  id: string;
  name: string;
  packId?: string;
  isFree: boolean;
  isBlackAndWhite?: boolean;
  cover: string;
  lut?: string;
  published: boolean;
  createdAt: string;
  createdById?: string;
  lastUpdatedAt?: string;
  lastUpdatedById?: string | null;
  firebaseId?: string | null;
  displayOrder?: number | null;
  translations?: Array<{
    about: string;
  }>;
  pack?: {
    id: string;
    name: string;
    packsCreators?: Array<{
      creators: {
        id: string;
        name: string;
        avatar: string;
        cover: string;
      };
    }>;
  };
}

export default class PresetService implements IService {
  [key: string]: ActionKeyPair<unknown, unknown>;

  getPresets: ActionKeyPair<GetPresetsParams, PaginatedResponse<IPreset>> = {
    key: "get-presets",
    actionBuilder: (params) => async () => {
      const response = await api({
        url: "/v1/presets",
        method: "GET" as Method,
        params,
      });

      return response;
    },
  };

  getPresetById: ActionKeyPair<{ id: string }, any> = {
    key: "get-preset-by-id",
    actionBuilder:
      ({ id }) =>
        async () => {
          return api({
            url: `/v1/presets/${id}`,
            method: "GET" as Method,
          });
        },
  };

  createPreset: ActionKeyPair<
    {
      data: Omit<CreatePresetPayload, "cover" | "lut"> & {
        cover: File;
        lut: File;
      };
    },
    PresetResponse
  > = {
      key: "create-preset",
      actionBuilder:
        ({ data }) =>
          async () => {
            const formData = new FormData();
            const { cover, lut, ...rest } = data;
            formData.append("cover", cover);
            formData.append("lut", lut);
            formData.append("data", JSON.stringify(rest));
            return api({
              url: "/v1/presets",
              method: "POST" as Method,
              data: formData,
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });
          },
    };

  updatePreset: ActionKeyPair<
    {
      id: string;
      data: Omit<UpdatePresetPayload, "cover" | "lut"> & {
        cover?: File;
        lut?: File;
      };
    },
    PresetResponse
  > = {
      key: "update-preset",
      actionBuilder:
        ({ id, data }) =>
          async () => {
            const formData = new FormData();
            const { cover, lut, ...rest } = data;
            if (cover) formData.append("cover", cover);
            if (lut) formData.append("lut", lut);
            formData.append("data", JSON.stringify(rest));
            return api({
              url: `/v1/presets?id=${id}`,
              method: "POST" as Method,
              data: formData,
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });
          },
    };

  removePreset: ActionKeyPair<
    { id: string },
    { id: string; deleted: boolean }
  > = {
      key: "remove-preset",
      actionBuilder:
        ({ id }) =>
          async () => {
            return api({
              url: `/v1/presets/${id}`,
              method: "DELETE" as Method,
            });
          },
    };

  /**
   * Busca as galleries de um preset via GET /v1/presets/{id}/galleries
   */
  getPresetGalleries: ActionKeyPair<{ id: string }, GalleryItem[]> = {
    key: "get-preset-galleries",
    actionBuilder:
      ({ id }) =>
        async () => {
          return api({
            url: `/v1/presets/${id}/galleries`,
            method: "GET" as Method,
          });
        },
  };

  /**
   * Upload das galleries de um preset via POST /v1/presets/{id}/galleries
   */
  uploadPresetGalleries: ActionKeyPair<
    { id: string; galleries: GalleryItem[] },
    UploadGalleriesResponse
  > = {
      key: "upload-preset-galleries",
      actionBuilder:
        ({ id, galleries }) =>
          async () => {
            return api({
              url: `/v1/presets/${id}/galleries`,
              method: "POST" as Method,
              data: { galleries },
            });
          },
    };

  /**
   * Upload de um item de galeria via POST /v1/presets-galleries/
   */
  uploadPresetGalleryItem: ActionKeyPair<
    {
      beforeFile: File;
      afterFile: File;
      data: {
        presetId: string;
        before: string;
        after: string;
        createdById: string;
        displayOrder: number;
      };
    },
    void
  > = {
      key: "upload-preset-gallery-item",
      actionBuilder:
        ({ beforeFile, afterFile, data }) =>
          async () => {
            const formData = new FormData();
            formData.append("beforeFile", beforeFile);
            formData.append("afterFile", afterFile);
            formData.append("data", JSON.stringify(data));
            return api({
              url: "/v1/preset-galleries",
              method: "POST" as Method,
              data: formData,
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });
          },
    };

  /**
   * Deleta uma galeria pelo UUID via DELETE /v1/preset-galleries/{galleryUuid}
   */
  deletePresetGallery: ActionKeyPair<
    { galleryUuid: string },
    { success: boolean }
  > = {
      key: "delete-preset-gallery",
      actionBuilder:
        ({ galleryUuid }) =>
          async () => {
            return api({
              url: `/v1/preset-galleries/${galleryUuid}`,
              method: "DELETE" as Method,
            });
          },
    };

  static Instance = new PresetService();
}
